// Dil tercihi — kalıcılık + uygulama seviyesi çözüm sırası.
//
// locales.ts'teki resolveLocale ADR-006 zincirini uyguluyor ama tabanı SDK
// kaynak dili en-US. Ürün yüzeylerinin çoğu TR-first ve her biri bu tabanı
// kendi içinde ayrı ayrı eziyordu: customer-web bir "resolved === DEFAULT ve
// tarayıcı 'en' demiyorsa tr-TR'ye çevir" yaması taşıyordu, website/kiosk/
// mobile lng'yi doğrudan sabitliyordu, operator-console hiç ezmediği için
// İngilizce açılıyordu. Altı yüzey, altı farklı cevap.
//
// resolveAppLocale tabanı çağırana parametre yapar: zincirin geri kalanı
// tek yerde kalır, "bu uygulamanın pazar dili nedir" sorusunu uygulama
// cevaplar. Yamalara gerek kalmaz.
//
// Kalıcılık ayrı bir eksendi: HİÇBİR yüzey kullanıcının seçimini
// saklamıyordu (website'in footer seçici'si bile yenilemede sıfırlanıyordu).
// Açık seçim token claim'ini de ezer — kullanıcı TR seçtiyse Keycloak
// profilinde ne yazdığı önemli değildir.

import { DEFAULT_LOCALE, isSupportedLocale, resolveLocale, type LocaleCode } from "./locales.ts";

/** Sürüm ekli: ileride şema değişirse eski değer sessizce yorumlanmaz. */
export const LOCALE_STORAGE_KEY = "novemai.locale.v1";

/**
 * localStorage / AsyncStorage-benzeri asgari arayüz. Web'de `localStorage`
 * doğrudan uyar; React Native tarafı ince bir adapter geçirir.
 */
export interface LocaleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Kullanıcının açıkça seçtiği dil. Depo okunamazsa (Safari private mode,
 * devre dışı çerezler, SSR) undefined döner — bu bir hata değil, "seçim
 * yok" demektir ve zincir bir sonraki adaya geçer.
 */
export function readStoredLocale(storage: LocaleStorage | undefined): LocaleCode | undefined {
  if (storage === undefined) return undefined;
  let raw: string | null;
  try {
    raw = storage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  // Desteklenmeyen bir değer (elle kurcalanmış ya da kaldırılmış bir locale)
  // sessizce yok sayılır; supportedLngs dışı bir lng i18next'i boş çeviriye
  // düşürürdü.
  return isSupportedLocale(raw) ? raw : undefined;
}

/** Yazma başarısızlığı sessiz: dil yine değişir, sadece hatırlanmaz. */
export function storeLocale(locale: LocaleCode, storage: LocaleStorage | undefined): void {
  if (storage === undefined) return;
  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* kota dolu / private mode — kalıcılık en iyi çaba */
  }
}

/**
 * Son ÇÖZÜLEN dilin önbelleği — açık seçimden ayrı anahtar, bilerek.
 *
 * Boot anında token claim'i yoktur (oturum henüz çözülmemiştir), bu yüzden ilk
 * boyama claim'i göremez ve tarayıcıya düşer: TR seçmiş bir kullanıcı her
 * yüklemede önce İngilizce bir ekran görür, /v1/me gelince arayüz dil
 * değiştirir. Claim çözülünce sonucu buraya yazıp bir sonraki boot'ta
 * `userPref` yuvasında okuyoruz — yani ilk boyama, claim'in ne diyeceğinin
 * TAHMİNİDİR, dördüncü bir kavram değil.
 *
 * LOCALE_STORAGE_KEY'e yazmak kestirme ama yanlış olurdu: o anahtar
 * "kullanıcı bu cihazda açıkça şunu seçti" demektir ve zincirin tepesinde
 * durur. Claim'i oraya yazmak, Keycloak tarafında yapılan bir dil değişikliğini
 * bir daha asla uygulanamaz hâle getirirdi.
 */
export const RESOLVED_LOCALE_CACHE_KEY = "novemai.locale.resolved.v1";

export function readCachedResolvedLocale(
  storage: LocaleStorage | undefined,
): LocaleCode | undefined {
  if (storage === undefined) return undefined;
  let raw: string | null;
  try {
    raw = storage.getItem(RESOLVED_LOCALE_CACHE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  return isSupportedLocale(raw) ? raw : undefined;
}

export function cacheResolvedLocale(
  locale: LocaleCode,
  storage: LocaleStorage | undefined,
): void {
  if (storage === undefined) return;
  try {
    storage.setItem(RESOLVED_LOCALE_CACHE_KEY, locale);
  } catch {
    /* en iyi çaba — önbellek yoksa yalnızca ilk boyama tahmini kaybolur */
  }
}

export interface ResolveAppLocaleOptions {
  /** Kullanıcının bu cihazda açıkça seçtiği dil — her şeyi ezer. */
  readonly stored?: string | undefined;
  /** Token `locale` claim'i ya da profil tercihi. */
  readonly userPref?: string | undefined;
  /** entity_tenant.default_locale. */
  readonly tenantDefault?: string | undefined;
  /** navigator.languages / expo-localization çıktısı. */
  readonly browserPreferences?: readonly string[] | undefined;
  /** Hiçbir aday tutmazsa uygulamanın pazar dili. Verilmezse SDK kaynağı. */
  readonly appDefault?: LocaleCode | undefined;
}

/**
 * stored → userPref → tenantDefault → browser → appDefault.
 *
 * Adaylar TEK TEK resolveLocale'e verilir; oradaki "önce tam eşleşme, sonra
 * dil-önekli eşleşme" mantığı (tr → tr-TR) tek kaynakta kalsın diye. Hepsini
 * birden vermek işe yaramazdı: resolveLocale eşleşme bulamayınca en-US
 * döndürüyor ve "gerçekten eşleşti mi yoksa tabana mı düştü" ayrımı
 * kayboluyor — appDefault'u uygulayamazdık.
 */
export function resolveAppLocale(opts: ResolveAppLocaleOptions): LocaleCode {
  const candidates: readonly (string | undefined)[] = [
    opts.stored,
    opts.userPref,
    opts.tenantDefault,
    ...(opts.browserPreferences ?? []),
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== "string" || candidate.length === 0) continue;
    const resolved = resolveLocale({ userPref: candidate });
    // resolveLocale taban olarak DEFAULT_LOCALE veriyor; adayın gerçekten
    // en-US'i işaret ettiğini doğrulamadan kabul edersek her çöp değer
    // İngilizce'ye çözülür.
    if (resolved !== DEFAULT_LOCALE || pointsToDefault(candidate)) {
      return resolved;
    }
  }
  return opts.appDefault ?? DEFAULT_LOCALE;
}

function pointsToDefault(candidate: string): boolean {
  if (isSupportedLocale(candidate)) return candidate === DEFAULT_LOCALE;
  const language = candidate.split("-")[0];
  return language !== undefined && DEFAULT_LOCALE.startsWith(`${language}-`);
}
